import { Deserializable } from '../../protocols/deserializable';
import { Section } from './section';
import { MenuStyle } from './menu-style';
import { Size } from '../../shared/size';
import { DisplayOptions } from '../../shared/display-options';
import { VariantFeature } from '../../product/dto/variant-feature';
import { Cachable } from '../../protocols/cachable';
import { DateUtils } from '../../../utils/date-utils';
import { Orderable } from '../../protocols/orderable';
import { Tag } from './tag';
import { environment } from '../../../../environments/environment';
import { OptionScale } from '../../enum/dto/option-scale.enum';
import { MenuOptions } from './menu-options';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { Asset } from '../../image/dto/asset';
import { Pagable } from '../../protocols/pagable';
import { OverflowState } from '../../../utils/overflow-state-type';
import { MenuType } from '../../../utils/menu-type-definition';
import { SmartFilterUtils } from '../../../utils/smart-filter-utils';
import { PillItem, PillItemInterface } from '../../shared/stylesheet/pill-item';
import { SelectablePill } from '@mobilefirstdev/reactive-form';

export class Menu
  implements Deserializable, Cachable, Orderable, UniquelyIdentifiable, Pagable, PillItemInterface, SelectablePill
{
  public id: string;
  public name: string = '';
  public companyId: number;
  public locationId: number;
  public companyLogo: Asset;
  public backgroundImage: Asset;
  public type: MenuType = MenuType.DisplayMenu;
  public theme: string;
  public sections: Section[];
  public styling: MenuStyle[];
  public displaySize: Size;
  public active: boolean;
  public configurationTitle: string;
  public tag: string = '';
  public options: DisplayOptions = new DisplayOptions();
  public menuOptions: MenuOptions = new MenuOptions();
  public variantFeature: VariantFeature;
  public overflowState: OverflowState = OverflowState.NONE;
  public lastModified: number;
  // Cache
  public cachedTime: number;
  // Display Properties
  public displayPriority: number;

  static newProductMenu(): Menu {
    const m = new Menu();
    m.name = '';
    m.tag = '';
    m.theme = null;
    m.active = true;
    return m;
  }

  static newMarketingMenu(): Menu {
    const m = new Menu();
    m.name = '';
    m.tag = '';
    m.theme = null;
    m.options = new DisplayOptions();
    m.options.scale = OptionScale.Fit;
    m.active = true;
    return m;
  }

  static getUniqueTags(menus: Menu[]): Tag[] {
    return menus
      ?.map(map => new Tag(map.tag))
      .filter((v, i, self) => self.findIndex(me => me.value === v.value) === i && v.value !== '');
  }

  static buildArrayCacheKey(locationId): string {
    return `Menus-${locationId}`;
  }

  static buildCacheKey(companyId: number, id: string): string {
    return `Menu-${companyId}-${id}`;
  }

  public onDeserialize() {
    this.companyLogo = window.injector.Deserialize.instanceOf(Asset, this.companyLogo);
    this.backgroundImage = window.injector.Deserialize.instanceOf(Asset, this.backgroundImage);
    this.sections = window.injector.Deserialize.arrayOf(Section, this.sections);
    this.styling = window.injector.Deserialize.arrayOf(MenuStyle, this.styling);
    this.displaySize = window.injector.Deserialize.instanceOf(Size, this.displaySize);
    this.options = window.injector.Deserialize.instanceOf(DisplayOptions, this.options);
    this.menuOptions = window.injector.Deserialize.instanceOf(MenuOptions, this.menuOptions);
    this.variantFeature = window.injector.Deserialize.instanceOf(VariantFeature, this.variantFeature);
    this.overflowState = this.overflowState || OverflowState.NONE;
    this.checkForEmptyDisplaySize();
  }

  setFeaturedVariantIds(ids: string[]) {
    this.variantFeature.variantIds = ids;
  }

  getFeaturedVariantIds(): string[] {
    if (this.variantFeature?.variantIds) {
      return this.variantFeature?.variantIds;
    } else {
      return [];
    }
  }

  getSelectionTitle(): string {
    return this.name;
  }

  getSelectionValue(): any {
    return this;
  }

  getSelectionUniqueIdentifier(): any {
    return this.id;
  }

  cacheExpirySeconds(): number {
    return DateUtils.secondsInOneHour();
  }

  cacheKey(): string {
    return Menu.buildCacheKey(this.companyId, this.id);
  }

  isExpired(): boolean {
    const expiresAt = this.cachedTime + this.cacheExpirySeconds();
    return DateUtils.currentTimestampInSeconds() > expiresAt;
  }

  // Orderable

  getOrderValue(): number {
    return this.displayPriority;
  }

  getOrderableTitle(): string {
    return this.name;
  }

  getOrderableUniqueId(): string {
    return this.id;
  }

  setOrderableValue(val: number) {
    this.displayPriority = val;
  }

  removeAssetOptions(mediaIdentifier: string) {
    this.options?.removeId(mediaIdentifier);
  }

  /**
   * Features use (uppercased)vId, while options use (lowercased)vId.fileType as key
   */
  removeFeaturedProduct(vId: string) {
    // Remove featured variantId
    const i = this.variantFeature.variantIds.findIndex(it => it.toUpperCase() === vId.toUpperCase());
    if (i > -1) {
      this.variantFeature.variantIds.splice(i, 1);
    }
    this.variantFeature?.removeId(vId);
    this.options?.removeId(vId);
  }

  private checkForEmptyDisplaySize() {
    if (
      !this.displaySize?.name &&
      !this.displaySize?.unit &&
      !this.displaySize?.orientation &&
      this.displaySize?.height === 0 &&
      this.displaySize?.width === 0
    ) {
      this.displaySize = null;
    }
  }

  public generateMenuUrl(locationId: number): string {
    const stage = environment.production ? '' : `${environment.environmentName}.`;
    switch (this.type) {
      case MenuType.PrintMenu:
        return `https://${stage}display.mybudsense.com/#/print/${locationId.toString()}/${this.id}`;
      case MenuType.WebMenu:
        return `https://${stage}display.mybudsense.com/#/web/${locationId.toString()}/${this.id}`;
      default:
        // Web and Digital menus use the same url
        return `https://${stage}display.mybudsense.com/#/menu/${locationId.toString()}/${this.id}`;
    }
  }

  getUniqueIdentifier(...opts: any): string {
    return '';
  }

  public hasSmartFilters(): boolean {
    return this.sections?.some(s => s?.hasSmartFilters());
  }

  getStartKey(): string {
    return this.id;
  }

  getAsPillItem(clickable: boolean, selected: boolean, disabled: boolean): PillItem {
    const imgSrc = SmartFilterUtils.getAppliedOnPillIcon(this.type);
    return new PillItem(this.name, clickable, selected, disabled, imgSrc, this);
  }
}
